import { useState } from "react";
import { useNotification } from "@context/NotificationContext";
import { getCurrentMovementService } from "@services/movementServicesApi";
import { getPortServices } from "@services/portServicesApi";
import type { ShippingAgencyTransferPayload } from "@services/shipping-agency-transfer/types";
import { postShippingAgencyTransfer } from "@services/shippingAgencyTransferApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import mapRequestShippingAgencyTransferFormToPayload from "../mappers/shippingAgencyTransferMapper";
import { useShippingAgencyTransferStore } from "../store/useRequestShippingAgencyTransfer.store";

type useCreateShippingAgencyProps = {
  vesselCallId: string;
  ownersMatters?: string;
  vesselId?: string;
  resetForm: () => void;
  vesselCallStatus?: string;
  departureStatus?: string;
};

const LANG_BASE = "vessel-call.shipping-agency-transfer.notifications";
const PORT_SERVICES_STATUS = {
  PENDING_APPROVAL: "PENDING_APPROVAL",
  STARTED: "STARTED",
} as const;

const DEPARTURE_IN_PROGRESS_STATUSES = [
  "DEPARTURE_AWAITING_MARINE_APPROVAL",
  "DEPARTURE_REQUESTED",
  "DEPARTURE_AWAITING_TERMINAL_APPROVAL",
  "MARINE_DEPARTMENT_AWAITING_APPROVAL",
  "PORT_TERMINAL_AWAITING_APPROVAL",
  "DEPARTURE_APPROVED",
];

const INTERRUPTED_STATUSES = ["INTERRUPTED_IN_PORT", "INTERRUPTED_LEAVE_PORT", "RESUME_PENDING"];

export const useCreateShippingAgencyTransfer = (props: useCreateShippingAgencyProps) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const queryClient = useQueryClient();

  const { vesselCallId, resetForm, vesselId, vesselCallStatus, departureStatus } = props;
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (payload: ShippingAgencyTransferPayload) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "ShippingAgencyTransfer",
          action: "submit_shipping_agency_transfer",
          entityType: "shipping_agency_transfer",
        },
        (transactionId) =>
          postShippingAgencyTransfer({
            service: "bff-vessel-call",
            shippingAgencyTransferPayload: payload,
            ...(transactionId ? { transactionId } : {}),
          }),
      ),
    onSuccess: () => {
      setError(null);
      resetForm();
      notify({
        title: t(`${LANG_BASE}.request-submitted-success-title`),
        description: t(`${LANG_BASE}.request-submitted-success-description`),
        type: "success",
      });

      queryClient.invalidateQueries({
        queryKey: ["getShippingAgencyTransfers", vesselCallId],
      });
    },
    onError: (err: any) => {
      notify({
        title: t(`${LANG_BASE}.request-submitted-error-title`),
        description:
          err?.response?.data?.message ??
          err?.message ??
          t(`${LANG_BASE}.request-submitted-error-description`),
        type: "error",
      });
    },
  });

  const onSubmit = async () => {
    const isDepartureInProgress =
      DEPARTURE_IN_PROGRESS_STATUSES.includes(vesselCallStatus ?? "") ||
      DEPARTURE_IN_PROGRESS_STATUSES.includes(departureStatus ?? "");

    const isDeparted = vesselCallStatus === "DEPARTED";
    const isDepartureClosed = vesselCallStatus === "CLOSED";

    const isInterrupted = INTERRUPTED_STATUSES.includes(vesselCallStatus ?? "");

    if (isInterrupted) {
      setError(t(`${LANG_BASE}.interrupted-error`));
      return;
    }

    if (isDepartureInProgress) {
      setError(t(`${LANG_BASE}.departure-in-progress-error`));
      return;
    }

    if (isDeparted) {
      setError(t(`${LANG_BASE}.departure-completed-six-hours-error`));
      return;
    }

    if (isDepartureClosed) {
      setError(t(`${LANG_BASE}.departure-closed-error`));
      return;
    }

    const activePortServices = await getPortServices({
      service: "bff-vessel-call",
      filters: {
        vesselCallId: vesselCallId,
      },
    });

    const activeMovement = await getCurrentMovementService({
      service: "bff-vessel-call",
      vesselCallId: vesselCallId,
      vesselId: vesselId ?? "",
    });

    const hasActiveMovement = activeMovement?.length > 0;

    const hasActivePortService = activePortServices.items.some(
      (service) =>
        service.status === PORT_SERVICES_STATUS.PENDING_APPROVAL ||
        service.status === PORT_SERVICES_STATUS.STARTED,
    );

    if (hasActiveMovement && hasActivePortService) {
      setError(t(`${LANG_BASE}.active-services-error`));
      return;
    } else if (hasActiveMovement) {
      setError(t(`${LANG_BASE}.active-movement-services-error`));
      return;
    } else if (hasActivePortService) {
      setError(t(`${LANG_BASE}.active-port-services-error`));
      return;
    } else {
      setError(null);
      const state = useShippingAgencyTransferStore.getState();
      const payload = mapRequestShippingAgencyTransferFormToPayload(vesselCallId, state);
      mutation.mutate(payload);
    }
  };

  return { onSubmit, error };
};
